import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, AlertCircle, CheckCircle, Clock, TrendingUp } from 'lucide-react';

const RecommendationCards = ({ recommendations, showAll = false }) => {
  if (!recommendations || recommendations.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <p className="text-gray-500 dark:text-gray-400">No recommendations available</p>
      </motion.div>
    );
  }

  const displayedRecommendations = showAll ? recommendations : recommendations.slice(0, 3);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-300';
      case 'low':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 border-green-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border-gray-300';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'cognitive':
        return <Lightbulb className="h-5 w-5" />;
      case 'behavioral':
        return <TrendingUp className="h-5 w-5" />;
      case 'study_strategy':
        return <CheckCircle className="h-5 w-5" />;
      default:
        return <AlertCircle className="h-5 w-5" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'cognitive':
        return 'from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20';
      case 'behavioral':
        return 'from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20';
      case 'study_strategy':
        return 'from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20';
      default:
        return 'from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Lightbulb className="h-6 w-6 text-yellow-500" />
          Personalized Recommendations
        </h3>
        {!showAll && recommendations.length > 3 && (
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Showing {displayedRecommendations.length} of {recommendations.length}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayedRecommendations.map((recommendation, index) => (
          <motion.div
            key={recommendation.id || index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`bg-gradient-to-br ${getTypeColor(recommendation.type)} rounded-xl shadow-lg p-6 border-2 ${getPriorityColor(recommendation.priority)}`}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className={`p-2 rounded-lg ${getPriorityColor(recommendation.priority)}`}>
                  {getTypeIcon(recommendation.type)}
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(recommendation.priority)}`}>
                  {recommendation.priority}
                </span>
              </div>
            </div>

            {/* Title */}
            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              {recommendation.title}
            </h4>

            {/* Description */}
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {recommendation.description}
            </p>

            {/* Action Items */}
            {recommendation.actionItems && recommendation.actionItems.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Action Steps:
                </p>
                <ul className="space-y-1">
                  {recommendation.actionItems.slice(0, 3).map((item, idx) => (
                    <li key={idx} className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-2">
                      <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0 text-green-600" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                <Clock className="h-3 w-3" />
                <span>{recommendation.estimatedTimeToImplement || 'Varies'}</span>
              </div>
              <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                {recommendation.expectedImpact || 'High Impact'}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default RecommendationCards;
